import { CartAddEvent } from '@theme/events';

const FLY_MS = 780;
const LAND_TICK_MS = 200;
const DRAWER_DELAY_AFTER_LAND_MS = 150;
const DRAWER_OPEN_MS = 360;
const DRAWER_CLOSE_MS = 260;
const ADDED_LABEL_MS = 400;
const FLY_EASING = 'ease-in-out';
const GHOST_SIZE = 88;
const LAND_SCALE = 0.72;
const ARC_Y = 36;
const ARC_OFFSET = 0.5;

function sectionIds() {
  const ids = new Set();
  document.querySelectorAll('cart-items-component[data-section-id]').forEach((el) => {
    if (el.dataset.sectionId) ids.add(el.dataset.sectionId);
  });
  return [...ids];
}

function variantIdFromButton(btn) {
  const card = btn.closest('product-card');
  const checked = card?.querySelector('input[type="radio"]:checked[data-variant-id]');
  if (checked?.dataset.variantId) return String(checked.dataset.variantId);

  const link = card?.querySelector('a.product-card__link[href]');
  if (link?.href) {
    try {
      const url = new URL(link.href, window.location.origin);
      const fromUrl = url.searchParams.get('variant');
      if (fromUrl) return String(fromUrl);
    } catch (err) {
      console.error('vc-grid-atc variant url', err);
    }
  }

  return String(btn.getAttribute('data-variant-id') || '');
}

function cartHrefIsForbidden(href) {
  if (!href) return false;
  try {
    const url = new URL(href, window.location.origin);
    const path = url.pathname.replace(/\/+$/, '') || '/';
    return path === '/cart' || path === '/cart/add' || path.startsWith('/cart/add');
  } catch (err) {
    const path = String(href).split('?')[0].replace(/\/+$/, '');
    return path === '/cart' || path === '/cart/add' || path.endsWith('/cart') || path.endsWith('/cart/add');
  }
}

function onCartPage() {
  const path = String(window.location.pathname || '').replace(/\/+$/, '') || '/';
  return path === '/cart';
}

function openExistingDrawer() {
  if (onCartPage()) return;
  const drawer = document.querySelector('cart-drawer-component');
  if (drawer && typeof drawer.open === 'function') {
    drawer.open();
    return;
  }
  if (drawer && typeof drawer.showDialog === 'function') {
    drawer.showDialog();
    return;
  }
  const dialog = drawer?.querySelector?.('dialog');
  if (dialog && typeof dialog.showModal === 'function' && !dialog.open) {
    dialog.showModal();
  }
}

function scheduleDrawerOpen(startedAt, reduced) {
  const wait = reduced
    ? 0
    : Math.max(0, FLY_MS + DRAWER_DELAY_AFTER_LAND_MS - (performance.now() - startedAt));
  window.setTimeout(openExistingDrawer, wait);
}

function prefersReducedMotion() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function isDisplayed(el) {
  if (!el || !(el instanceof Element)) return false;
  if (el.closest('dialog:not([open])')) return false;
  if (el.closest('[hidden]')) return false;
  const r = el.getBoundingClientRect();
  if (r.width < 8 || r.height < 8) return false;
  const style = window.getComputedStyle(el);
  if (style.display === 'none' || style.visibility === 'hidden') return false;
  return true;
}

function firstVisibleImg(root, selector) {
  const scope = root || document;
  const nodes = scope.querySelectorAll(selector);
  for (const img of nodes) {
    if (img instanceof HTMLImageElement && isDisplayed(img)) return img;
  }
  return null;
}

function flyStartEl(sourceEl) {
  if (!sourceEl || !(sourceEl instanceof Element)) return null;

  const card = sourceEl.closest('product-card');
  if (card) {
    const gallery = card.querySelector('.card-gallery') || card;
    const img = firstVisibleImg(gallery, 'img') || gallery.querySelector('img');
    if (img) return img;
  }

  const pdpImg =
    firstVisibleImg(document, 'media-gallery .product-media:not([hidden]) .product-media__image') ||
    firstVisibleImg(document, '.product-media__image') ||
    firstVisibleImg(document, 'media-gallery img, .product-media img');
  if (pdpImg) return pdpImg;

  if (sourceEl instanceof HTMLFormElement) {
    const submit = sourceEl.querySelector('button[type="submit"], .add-to-cart-button');
    if (submit) return submit;
  }

  return sourceEl;
}

function markGridAdded(btn) {
  if (!btn) return;
  const prev = btn.getAttribute('aria-label') || '';
  btn.classList.add('vc-grid-atc--added');
  btn.setAttribute('aria-label', 'Added');
  window.clearTimeout(btn._vcAddedTimer);
  btn._vcAddedTimer = window.setTimeout(() => {
    btn.classList.remove('vc-grid-atc--added');
    if (prev) btn.setAttribute('aria-label', prev);
  }, ADDED_LABEL_MS);
}

function markPdpAdded(form) {
  const btn = form?.querySelector?.('[ref="addToCartButton"], button[type="submit"], .add-to-cart-button');
  if (!btn) return;
  const content =
    btn.querySelector('.add-to-cart-text__content span span') ||
    btn.querySelector('.add-to-cart-text__content');
  if (!content) return;
  if (content.dataset.vcOrig == null) content.dataset.vcOrig = content.textContent || 'Add to cart';
  content.textContent = 'ADDED';
  window.clearTimeout(btn._vcAddedTimer);
  btn._vcAddedTimer = window.setTimeout(() => {
    content.textContent = content.dataset.vcOrig || 'Add to cart';
  }, ADDED_LABEL_MS);
}

function showAddedState(sourceEl) {
  if (sourceEl instanceof HTMLFormElement) {
    markPdpAdded(sourceEl);
    return;
  }
  const circle = sourceEl?.matches?.('[data-vc-grid-atc]')
    ? sourceEl
    : sourceEl?.closest?.('[data-vc-grid-atc]');
  if (circle) markGridAdded(circle);
}

function tickPlusOne(dest) {
  if (!dest || prefersReducedMotion()) return;
  const to = dest.getBoundingClientRect();
  const el = document.createElement('span');
  el.className = 'vc-cart-plus1';
  el.textContent = '+1';
  el.setAttribute('aria-hidden', 'true');
  el.style.cssText = [
    'position:fixed',
    `left:${to.left + to.width / 2}px`,
    `top:${to.top - 2}px`,
    'transform:translate(-50%,-100%)',
    'font-size:16px',
    'font-weight:700',
    'letter-spacing:0.02em',
    'color:#1a1512',
    'z-index:2147483001',
    'pointer-events:none',
    'line-height:1',
    'white-space:nowrap',
    'opacity:0',
  ].join(';');
  document.body.appendChild(el);
  const fadeIn = el.animate([{ opacity: 0 }, { opacity: 1 }], {
    duration: LAND_TICK_MS,
    easing: 'ease-out',
    fill: 'forwards',
  });
  const done = () => el.remove();
  fadeIn.finished
    .then(() =>
      el
        .animate([{ opacity: 1 }, { opacity: 0 }], {
          duration: LAND_TICK_MS,
          easing: 'ease-in',
          fill: 'forwards',
        })
        .finished.then(done)
        .catch(done)
    )
    .catch(done);
}

function flyItem(sourceEl) {
  showAddedState(sourceEl);
  if (prefersReducedMotion()) return;

  const dest = document.querySelector('[data-testid="cart-drawer-trigger"]');
  if (!dest) return;

  const startEl = flyStartEl(sourceEl) || sourceEl;
  if (!startEl || typeof startEl.getBoundingClientRect !== 'function') return;

  const from = startEl.getBoundingClientRect();
  const to = dest.getBoundingClientRect();
  const fromCx = from.left + from.width / 2;
  const fromCy = from.top + from.height / 2;
  const toCx = to.left + to.width / 2;
  const toCy = to.top + to.height / 2;
  const startLeft = fromCx - GHOST_SIZE / 2;
  const startTop = fromCy - GHOST_SIZE / 2;
  const dx = toCx - fromCx;
  const dy = toCy - fromCy;
  const midX = dx * ARC_OFFSET;
  const midY = dy * ARC_OFFSET - ARC_Y;
  const midScale = 1 + (LAND_SCALE - 1) * ARC_OFFSET;

  const src =
    startEl instanceof HTMLImageElement ? startEl.currentSrc || startEl.src || '' : '';
  const ghost = document.createElement(src ? 'img' : 'div');
  ghost.className = 'vc-fly-thumb';
  ghost.setAttribute('aria-hidden', 'true');
  if (ghost instanceof HTMLImageElement) {
    ghost.src = src;
    ghost.alt = '';
    ghost.draggable = false;
  }

  ghost.style.cssText = [
    'position:fixed',
    `left:${startLeft}px`,
    `top:${startTop}px`,
    `width:${GHOST_SIZE}px`,
    `height:${GHOST_SIZE}px`,
    'object-fit:cover',
    'border-radius:10px',
    'border:2px solid #1a1512',
    'background:#fff',
    'box-shadow:0 12px 32px rgba(26,21,18,0.32)',
    'z-index:2147483000',
    'pointer-events:none',
    'margin:0',
    'padding:0',
    'display:block',
    'opacity:1',
    'transform:translate(0,0) scale(1)',
  ].join(';');
  document.body.appendChild(ghost);

  const anim = ghost.animate(
    [
      { transform: 'translate(0px, 0px) scale(1)', opacity: 1 },
      {
        transform: `translate(${midX}px, ${midY}px) scale(${midScale})`,
        opacity: 1,
        offset: ARC_OFFSET,
      },
      { transform: `translate(${dx}px, ${dy}px) scale(${LAND_SCALE})`, opacity: 1 },
    ],
    { duration: FLY_MS, easing: FLY_EASING, fill: 'forwards' }
  );

  window.setTimeout(() => {
    tickPlusOne(dest);
    dest.animate(
      [
        { transform: 'scale(1)' },
        { transform: 'scale(1.18)' },
        { transform: 'scale(1)' },
      ],
      { duration: 280, easing: 'ease-out' }
    );
  }, FLY_MS);

  const done = () => ghost.remove();
  anim.finished
    .then(() => {
      return ghost.animate(
        [{ opacity: 1 }, { opacity: 0 }],
        { duration: 160, easing: 'ease-in', fill: 'forwards' }
      ).finished;
    })
    .then(done)
    .catch(done);
}

function buttonFromEvent(event) {
  const t = event.target;
  if (!(t instanceof Element)) return null;
  return t.closest('[data-vc-grid-atc]');
}

function stealClick(event, prevent) {
  const btn = buttonFromEvent(event);
  if (!btn) return null;
  if (prevent) event.preventDefault();
  event.stopPropagation();
  event.stopImmediatePropagation();
  return btn;
}

async function postAddJson(id, quantity = 1) {
  const body = {
    items: [{ id, quantity }],
    sections: sectionIds().join(','),
    sections_url: window.location.pathname,
  };
  const root = window.Shopify?.routes?.root || '/';
  const res = await fetch(`${root}cart/add.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) console.error('vc-grid-atc add failed', res.status);
  let data = {};
  try {
    data = await res.json();
  } catch (err) {
    console.error('vc-grid-atc json', err);
    return { ok: false, data: {} };
  }
  return { ok: true, data };
}

function dispatchAdd(data, id, source, extra = {}) {
  try {
    document.dispatchEvent(
      new CartAddEvent(data, source, {
        source,
        itemCount: data.item_count,
        variantId: String(id),
        sections: data.sections,
        ...extra,
      })
    );
  } catch (err) {
    console.error('vc-grid-atc CartAddEvent', err);
  }
}

async function addFromGrid(btn) {
  if (btn.disabled || btn.getAttribute('aria-disabled') === 'true') return;

  const id = Number(variantIdFromButton(btn));
  if (!id) {
    console.error('vc-grid-atc missing variant id');
    return;
  }

  const startedAt = performance.now();
  const reduced = prefersReducedMotion();
  flyItem(btn);
  btn.disabled = true;
  btn.setAttribute('aria-busy', 'true');

  let succeeded = false;
  try {
    const { ok, data } = await postAddJson(id, 1);
    succeeded = ok;
    dispatchAdd(data, id, 'vc-grid-atc', {
      productId: btn.getAttribute('data-product-id') || '',
    });
  } catch (err) {
    console.error('vc-grid-atc', err);
  } finally {
    if (succeeded) scheduleDrawerOpen(startedAt, reduced);
    btn.disabled = false;
    btn.removeAttribute('aria-busy');
  }
}

function isProductAddForm(form) {
  if (!(form instanceof HTMLFormElement)) return false;
  if (form.closest('[data-vc-grid-atc]')) return false;
  const action = form.getAttribute('action') || form.action || '';
  const type = form.getAttribute('data-type') || '';
  if (type === 'add-to-cart-form') return true;
  if (form.classList.contains('shopify-product-form')) return true;
  return cartHrefIsForbidden(action) && /\/cart\/add/.test(String(action));
}

async function addFromProductForm(form) {
  const idInput = form.querySelector('input[name="id"]');
  const id = Number(idInput?.value);
  if (!id) {
    console.error('vc-grid-atc product form missing variant id');
    return;
  }

  const startedAt = performance.now();
  const reduced = prefersReducedMotion();
  flyItem(form);
  const qtyInput = form.querySelector('input[name="quantity"], [name="quantity"]');
  const quantity = Math.max(1, Number(qtyInput?.value) || 1);
  const submitters = form.querySelectorAll('button[type="submit"], .add-to-cart-button');
  submitters.forEach((el) => {
    if ('disabled' in el) el.disabled = true;
  });

  let succeeded = false;
  try {
    const formData = new FormData(form);
    formData.set('id', String(id));
    formData.set('quantity', String(quantity));
    const ids = sectionIds().join(',');
    if (ids) formData.set('sections', ids);
    formData.set('sections_url', window.location.pathname);

    const root = window.Shopify?.routes?.root || '/';
    const res = await fetch(`${root}cart/add.js`, {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: formData,
    });
    if (!res.ok) console.error('vc-grid-atc pdp add failed', res.status);
    let data = {};
    try {
      data = await res.json();
      succeeded = true;
    } catch (err) {
      console.error('vc-grid-atc pdp json', err);
    }
    dispatchAdd(data, id, 'vc-pdp-atc');
  } catch (err) {
    console.error('vc-grid-atc pdp', err);
  } finally {
    if (succeeded) scheduleDrawerOpen(startedAt, reduced);
    submitters.forEach((el) => {
      if ('disabled' in el) el.disabled = false;
    });
  }
}

document.addEventListener(
  'pointerdown',
  (event) => {
    stealClick(event, false);
  },
  true
);

document.addEventListener(
  'click',
  (event) => {
    const btn = stealClick(event, true);
    if (btn) {
      addFromGrid(btn);
      return;
    }
    const t = event.target;
    if (!(t instanceof Element)) return;
    const form = t.closest('form');
    if (!form || !isProductAddForm(form)) return;
    if (!t.closest('button[type="submit"], .add-to-cart-button, add-to-cart-component')) return;
    event.stopPropagation();
    event.stopImmediatePropagation();
  },
  true
);

document.addEventListener(
  'submit',
  (event) => {
    const form = event.target;
    if (!isProductAddForm(form)) return;
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    addFromProductForm(form);
  },
  true
);

function applyDrawerTimings() {
  const root = document.documentElement;
  root.style.setProperty('--vc-drawer-open-ms', `${DRAWER_OPEN_MS}ms`);
  root.style.setProperty('--vc-drawer-close-ms', `${DRAWER_CLOSE_MS}ms`);
}
applyDrawerTimings();
